import GunbLayer from '../layers/GunbLayer';
import { removeSourceAndLayers } from '../utils/maplibre-helpers';

export function createGunbManager(config) {
    var state = config.state;
    var gunbDetailCache = {};
    var gunbDetailRequest = null;

    function setSelectedListingId(value) {
        if (typeof state.setSelectedListingId === 'function') {
            state.setSelectedListingId(value);
        }
    }

    function sameLatLng(a, b) {
        return !!a
            && !!b
            && Math.abs(a.lat - b.lat) <= 0.000001
            && Math.abs(a.lng - b.lng) <= 0.000001;
    }

    function updateSelectedCenter(center) {
        if (!center) return;

        var changed = !sameLatLng(state.getSelectedLatLng(), center);
        state.setSelectedLatLng(center);

        if (changed) {
            config.rebuildNearbyLinkIfNeeded();
        }

        if (state.getPendingNearbyRestore() && !state.getNearbyLoaded()) {
            config.loadNearbyForSelection(false);
        }
    }

    function abortGunbDetailRequest() {
        if (gunbDetailRequest && gunbDetailRequest.controller) {
            gunbDetailRequest.controller.abort();
            gunbDetailRequest = null;
        }
    }

    function findGunbFeatureById(gunbId) {
        var gunbController = config.getGunbController();
        if (!gunbController) return null;
        return gunbController.findFeatureById(gunbId);
    }

    function clearSelectedGunbVisual() {
        var gunbController = config.getGunbController();
        var map = config.getMap();

        if (gunbController) {
            gunbController.clearSelectedFeature();
        } else if (state.getSelectedGunbLayer() && map) {
            removeSourceAndLayers(map, GunbLayer.SELECTED_SOURCE_ID, GunbLayer.SELECTED_LAYER_IDS);
        }

        state.setSelectedGunbLayer(null);
    }

    function renderSelectedGunbVisual(feature) {
        var gunbController = config.getGunbController();
        if (!gunbController || !feature || !feature.geometry) {
            clearSelectedGunbVisual();
            return null;
        }

        var result = gunbController.renderSelectedFeature(feature);
        state.setSelectedGunbLayer(result ? result.layer : null);
        updateSelectedCenter(result ? result.center : null);
        config.bringDataLayersToFront();
        config.applySelectionFocusState();

        return state.getSelectedGunbLayer();
    }

    function syncSelectedGunbVisual() {
        var gunbController = config.getGunbController();
        if (!state.getSelectedGunbId() || !config.isGunbActive() || !config.hasGunbLayer()) {
            clearSelectedGunbVisual();
            config.applySelectionFocusState();
            return null;
        }

        var result = gunbController ? gunbController.syncSelectedFeature(state.getSelectedGunbId()) : null;
        if (!result) {
            clearSelectedGunbVisual();
            config.applySelectionFocusState();
            return null;
        }

        state.setSelectedGunbLayer(result.layer);
        updateSelectedCenter(result.center);
        config.bringDataLayersToFront();
        config.applySelectionFocusState();

        return state.getSelectedGunbLayer();
    }

    function browseGunbInPanel(latlng, features) {
        config.clearTapBrowseState();
        state.setOverlapBrowseFeatures(null);
        state.setOverlapBrowseLatlng(null);
        state.setCombinedBrowseTransactions(null);
        state.setCombinedBrowseGunb(null);
        state.setCombinedBrowseOtherHits(null);
        state.setCombinedBrowseLatlng(null);
        state.setGunbBrowseFeatures(features);
        state.setGunbBrowseLatlng(latlng);
        config.renderListPanel();
        config.setTab('list', { snap: 'half' });
    }

    function exitGunbBrowse() {
        state.setGunbBrowseFeatures(null);
        state.setGunbBrowseLatlng(null);
        config.renderListPanel({ immediate: true });
    }

    function fetchGunbDetail(id) {
        if (!id) return;

        var cacheKey = String(id);
        if (gunbDetailCache[cacheKey]) {
            if (String(state.getSelectedGunbId()) === cacheKey) {
                config.renderGunbDetails(gunbDetailCache[cacheKey]);
            }
            return;
        }

        abortGunbDetailRequest();

        var controller = new AbortController();
        var requestState = {
            controller: controller,
            id: cacheKey,
        };
        gunbDetailRequest = requestState;

        fetch(config.BASE_URL + 'api/gunb-rwdz-detail/' + id, { signal: controller.signal })
            .then(function(response) { return response.json(); })
            .then(function(data) {
                if (controller.signal.aborted || String(state.getSelectedGunbId()) !== cacheKey) {
                    return;
                }
                if (!data || data.error || !data.id) {
                    config.renderDetailsError('Nie znaleziono pozwolenia');
                    return;
                }

                gunbDetailCache[cacheKey] = data;
                config.renderGunbDetails(data);
            })
            .catch(function(error) {
                if (error && error.name === 'AbortError') {
                    return;
                }
                if (String(state.getSelectedGunbId()) === cacheKey) {
                    config.renderDetailsError('B\u0142\u0105d \u0142adowania szczeg\u00f3\u0142\u00f3w');
                }
            })
            .finally(function() {
                if (gunbDetailRequest === requestState) {
                    gunbDetailRequest = null;
                }
            });
    }

    function selectGunbPermit(feature, fallbackLatlng) {
        var props = feature && feature.properties ? feature.properties : {};
        if (!props.id) return;

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedGunbId(props.id);
        state.setSelectedTid(null);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedSignalContext(null);
        config.clearTapBrowseState();
        state.setSelectedLatLng(config.getFeatureCentroid(feature) || fallbackLatlng || state.getSelectedLatLng());
        state.setSelectedTransactionRow(null);
        config.clearTransactionHighlight();
        config.clearSelectedFeatureVisual();
        renderSelectedGunbVisual(feature);
        config.clearNearbyState();
        config.renderDetailsLoading('Pozwolenie na budow\u0119');
        fetchGunbDetail(props.id);
        config.applySelectedStylesInGunbList();
        config.applySelectionFocusState();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }

    return {
        browseGunbInPanel: browseGunbInPanel,
        clearSelectedGunbVisual: clearSelectedGunbVisual,
        exitGunbBrowse: exitGunbBrowse,
        fetchGunbDetail: fetchGunbDetail,
        findGunbFeatureById: findGunbFeatureById,
        renderSelectedGunbVisual: renderSelectedGunbVisual,
        selectGunbPermit: selectGunbPermit,
        syncSelectedGunbVisual: syncSelectedGunbVisual,
    };
}
